import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import {
  PlannedResource,
  usePlannedResourceOptions,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router';
import { selectIncidentGuid, useAppSelector } from 'slices/commonSelectors';
import { NULL_VALUE } from 'constants/displayConstants';

type AssignmentCardResourceDialogProps = {
  walaId: string;
  onClose: () => void;
  plannedResources: PlannedResource[] | null;
};

type IncidentResource = {
  name: string;
  kind: string;
  type: string;
  quantity: number;
  availability: string;
  location: string;
};

const AssignmentCardResourceDialog = ({
  walaId,
  onClose,
  plannedResources,
}: AssignmentCardResourceDialogProps) => {
  const { data: plannedResourceOptions } = usePlannedResourceOptions(walaId);

  const { mutate: update } = useUpdateWorkAssignmentListAttachment();

  const incidentResourceData = useMemo(() => {
    return (
      plannedResourceOptions?.map((res) => ({
        id: res.id,
        name: res.name || NULL_VALUE,
        kind: res.kind || NULL_VALUE,
        type: res.type || NULL_VALUE,
        quantity: res.quantity || 0,
        availability: NULL_VALUE, // Placeholder. This will be derived by establishing if this resource has been assigned to another work assignment for the operational period that provides the context for this dialog. That could possibly be determined by interrogating the work_assignment_id field, but this needs to be confirmed.
        location: res.location || 'Unknown',
      })) || []
    );
  }, [plannedResourceOptions]);

  const [selected, setSelected] = useState<Set<string>>(
    new Set(plannedResources?.map((resource) => resource.id) || [])
  );

  const getIsSelected = useCallback(
    (id: string) => {
      return selected.has(id);
    },
    [selected]
  );

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSubmit = () => {
    const plannedResourcesArr = plannedResources ?? [];
    const existingIds = new Set(
      plannedResourcesArr.map((resource) => resource.id)
    );
    const existingResources = plannedResourcesArr.filter(
      (resource) => resource.isDocumentation || selected.has(resource.id)
    );
    const plannedResouceOptionsArr = plannedResourceOptions ?? [];
    const newResources = plannedResouceOptionsArr
      .filter(
        (resource) => selected.has(resource.id) && !existingIds.has(resource.id)
      )
      .map((resource) => ({
        id: resource.id,
        incidentResourceId: resource.incidentResourceId,
        name: resource.name ?? '',
        leader: '',
        numberOfPersons: 0,
        contactInfo: '',
        reportingInfo: '',
        has204a: false,
      }));

    const updatedPlannedResources = [...existingResources, ...newResources];

    update({
      walaId,
      plannedResources: updatedPlannedResources,
    });
    onClose();
  };

  const columnHelper = createColumnHelper<IncidentResource>();
  const incidentResourceColumns = useMemo<ColumnDef<IncidentResource, any>[]>(
    () => [
      columnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={getIsSelected(row.id)}
            onChange={() => toggleSelected(row.id)}
          />
        ),
        meta: { align: 'center' },
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: () => <span>Resource Name</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.name}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.accessor('kind', {
        id: 'kind',
        header: () => <span>Kind</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.kind}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.accessor('type', {
        id: 'type',
        header: () => <span>Type</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.type}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.accessor('quantity', {
        id: 'quantity',
        header: () => <span>Quantity</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.quantity}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.accessor('availability', {
        id: 'availability',
        header: () => <span>Availability</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.availability}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.accessor('location', {
        id: 'location',
        header: () => <span>Location</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return <span>{row.location}</span>;
        },
        meta: { align: 'left' },
      }),
    ],
    [columnHelper, getIsSelected, toggleSelected]
  );

  const [searchText, setSearchText] = useState('');

  const filteredList = useMemo(() => {
    if (!searchText) return incidentResourceData;

    const lowerSearch = searchText.toLowerCase();
    return incidentResourceData.filter((item) => {
      return (
        item.name.toLowerCase().includes(lowerSearch) ||
        item.kind.toLowerCase().includes(lowerSearch) ||
        item.type.toLowerCase().includes(lowerSearch)
      );
    });
  }, [incidentResourceData, searchText]);

  const incidentId = useAppSelector(selectIncidentGuid);

  const resourcesLink = `/iap/${incidentId}/resources`;

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      size="lg"
      backdrop={true}
      keyboard={false}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-end p-2">
          <h4 className="m-0">Select Resources to Assign</h4>
          <Link to={resourcesLink} target="_blank" className="font-size--small">
            + Add New Resource
          </Link>
        </div>
        <StylishNewSearchBar
          value={searchText}
          onChangeSearchTerm={setSearchText}
          placeholder="Search resources by name, kind, or type..."
        />
        <div className="max-vh-75 overflow-auto">
          <PratusTable
            className={'layout-auto pratus-table'}
            columns={incidentResourceColumns}
            data={filteredList}
            getRowId={(row) => row.id}
            noDataIndication={
              <div className="d-flex justify-content-center flex-column align-items-center p-3">
                <div>
                  {incidentResourceData.length == 0
                    ? 'No resources yet. Add a new resource.'
                    : 'No resources fit your search. Add a new resource.'}
                </div>
              </div>
            }
          />
        </div>
        <div className="d-flex justify-content-start">
          <StylishNewButton onClick={() => handleSubmit()}>
            Add Selected Resources
          </StylishNewButton>
          <StylishNewButton
            type="button"
            className="ms-1 border border-neutral-500 bg-transparent"
            onClick={() => onClose()}
          >
            Close
          </StylishNewButton>
        </div>
      </div>
    </Modal>
  );
};

export default AssignmentCardResourceDialog;
